<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
const store = useAnalysisStore();

import ImgCenterDeco from '@manseImages/yongsin-light/img-yongsin-center-deco.svg?url';
import IconOakTree from '@manseImages/yongsin-light/icon-oak-tree.png';
import IconFire from '@manseImages/yongsin-light/icon-fire.png';
import IconMountain from '@manseImages/yongsin-light/icon-mountain.png';
import IconBattle from '@manseImages/yongsin-light/icon-battle.png';
import IconWater from '@manseImages/yongsin-light/icon-water.png';
import ArrowYongsin from '@manseImages/yongsin-light/arrow-yongsin.svg?url';
import ArrowHeesin from '@manseImages/yongsin-light/arrow-heesin.svg?url';
import ArrowLabel from '@manseImages/yongsin-light/arrow-label.svg?url';
import ImgCatGuide from '@manseImages/yongsin-light/img-yongsin-cat-guide.png';
import ImgChalkboard from '@manseImages/yongsin-light/img-yongsin-chalkboard.svg?url';
import ImgCatTeacher from '@manseImages/yongsin-light/img-yongsin-cat-teacher.png';

const props = defineProps({
  isMobile: Boolean,
  contentData: {
    type: Object,
    default: null,
  },
});

const userName = computed(() => store.userName || '-');

// ═══ 오행 색상 맵 ═══
const ELEMENT_COLOR_MAP = {
  목: '#5e9f1c',
  화: '#c23535',
  토: '#db9823',
  금: '#949494',
  수: '#404040',
};

// 오행별 센터 데코 색상 (용신 기운 배경)
const DECO_COLOR_MAP = {
  목: '#E6F5D6',
  화: '#FEE6D6',
  토: '#FEF0D6',
  금: '#F0F0F0',
  수: '#ECECEC',
};

// 오행별 하이라이트 색상 (이름 뒤 강조)
const HIGHLIGHT_COLOR_MAP = {
  목: '#C8E6A0',
  화: '#FFCBA4',
  토: '#FFDFA4',
  금: '#D9D9D9',
  수: '#D8D8D8',
};

// 오행별 오상(五常) 한자
const ELEMENT_HANJA_MAP = {
  목: { char: '仁', reading: '어질 인' },
  화: { char: '禮', reading: '예도 례' },
  토: { char: '信', reading: '믿을 신' },
  금: { char: '義', reading: '옳을 의' },
  수: { char: '智', reading: '지혜 지' },
};

// 오행별 자연물
const ELEMENT_NATURE_MAP = {
  목: '나무, 초목, 대나무',
  화: '태양, 불꽃, 빛',
  토: '산, 흙, 토대(땅)',
  금: '칼, 보석, 가위',
  수: '바다, 비, 이슬',
};

// ═══ 용신 정보 ═══
const yongsinInfo = computed(
  () => props.contentData?.yongsinInfo ?? { name: '', element: '', keyword: '' },
);

// ═══ 희신 정보 ═══
const heesinInfo = computed(
  () => props.contentData?.heesinInfo ?? { name: '', element: '', keyword: '' },
);

// 용신/희신 색상
const yongsinColor = computed(() => ELEMENT_COLOR_MAP[yongsinInfo.value.element] || '#db9823');
const heesinColor = computed(() => ELEMENT_COLOR_MAP[heesinInfo.value.element] || '#949494');

// 센터 데코 색상 (용신 오행 기반)
const decoColor = computed(() => DECO_COLOR_MAP[yongsinInfo.value.element] || '#FEF0D6');
const highlightColor = computed(() => HIGHLIGHT_COLOR_MAP[yongsinInfo.value.element] || '#FFDFA4');

// 용신 한자·자연물 (오행에서 자동 매핑, API 데이터 우선)
const yongsinHanja = computed(
  () => ELEMENT_HANJA_MAP[yongsinInfo.value.element] ?? { char: '', reading: '' },
);
const yongsinNature = computed(
  () => props.contentData?.yongsinNature ?? ELEMENT_NATURE_MAP[yongsinInfo.value.element] ?? '',
);
const heesinHanja = computed(
  () => ELEMENT_HANJA_MAP[heesinInfo.value.element] ?? { char: '', reading: '' },
);
const heesinNature = computed(
  () => props.contentData?.heesinNature ?? ELEMENT_NATURE_MAP[heesinInfo.value.element] ?? '',
);

// ═══ 오행별 아이콘 맵 ═══
const ELEMENT_ICON_MAP = {
  목: { src: IconOakTree, size: '80px' },
  화: { src: IconFire, size: '75px' },
  토: { src: IconMountain, size: '78px' },
  금: { src: IconBattle, size: '75px' },
  수: { src: IconWater, size: '65px' },
};

// ═══ 오행별 한자 표기 (카드 제목용) ═══
const ELEMENT_HANJA_TITLE_MAP = {
  목: '木',
  화: '火',
  토: '土',
  금: '金',
  수: '水',
};

// ═══ 오행별 오상 한글 읽기 (짧은 버전) ═══
const ELEMENT_VIRTUE_MAP = {
  목: '인',
  화: '예',
  토: '신',
  금: '의',
  수: '지',
};

// ═══ 오행 카드 설명 데이터 (API 데이터 기반 동적 생성) ═══
const ELEMENT_CARDS = computed(() => {
  const apiCards = props.contentData?.elementCardsData;
  if (apiCards) return apiCards;
  // fallback 없음: 데이터 미연결 시 빈 배열
  return [];
});

// ═══ 오행 비율 데이터 (사주구조 페이지에서 추출) ═══
const fiveElementRatios = computed(() => props.contentData?.fiveElementRatios ?? []);

// 오행별 비율 맵 (element → ratio)
const ratioMap = computed(() => {
  const map = {};
  fiveElementRatios.value.forEach((item) => {
    map[item.element] = item.ratio;
  });
  return map;
});

// ═══ 칠판 섹션 데이터 ═══
const ohengSummaryLines = computed(() => props.contentData?.ohengSummaryLines ?? []);

// 오행 철학배너 (말풍선)
const guideBanner = computed(() => props.contentData?.guideBanner ?? null);

// 칠판 하단 안심멘트
const ansimMessage = computed(() => props.contentData?.ansimMessage ?? '');

// 비율이 가장 낮은 오행 (하단 안내 문구용)
const lowestElement = computed(() => {
  if (!fiveElementRatios.value.length) return null;
  return [...fiveElementRatios.value].sort((a, b) => a.ratio - b.ratio)[0];
});

const lowestElementHanja = computed(() => {
  if (!lowestElement.value) return '';
  return ELEMENT_HANJA_TITLE_MAP[lowestElement.value.element] || '';
});

// 비율 바 애니메이션
const animated = ref(false);
onMounted(() => {
  setTimeout(() => {
    animated.value = true;
  }, 300);
});

// 용신 일러스트 설명
const yongsinDescription = computed(
  () => props.contentData?.yongsinDescription ?? '',
);

// ═══ 용신 설명 카드 텍스트 ═══
const yongsinDescParagraphs = computed(() => props.contentData?.yongsinDescParagraphs ?? null);

// ═══ 희신 설명 카드 텍스트 ═══
const heesinDescParagraphs = computed(() => props.contentData?.heesinDescParagraphs ?? null);

// ═══ 두 기운의 관계 ═══
const RELATION_BG_MAP = {
  목: '#f0f8ea',
  화: '#fef5f0',
  토: '#fcf8f0',
  금: '#f5f5f5',
  수: '#f0f2f5',
};

// 상생 관계 맵: A → B (A가 B를 생)
const SANGSAENG_MAP = {
  목: '화',
  화: '토',
  토: '금',
  금: '수',
  수: '목',
};

// API 데이터에서 두 기운의 관계 설명 추출
const relationData = computed(() => props.contentData?.relationDesc ?? null);

const relationBgColor = computed(
  () => RELATION_BG_MAP[yongsinInfo.value.element] || '#fcf8f0',
);

// 용신이 희신을 생하는지 확인
const isSangsaeng = computed(() => {
  const y = yongsinInfo.value.element;
  const h = heesinInfo.value.element;
  return SANGSAENG_MAP[y] === h;
});

// 설명 HTML (용신/희신 오행·상생 관계 하이라이트 처리 완료)
const relationDescHtml = computed(() => relationData.value?.descHtml || '');

// 실용조언 줄 배열
const relationTipLines = computed(() => relationData.value?.tipLines || []);

</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right">
        <!-- 타이틀 바 -->
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">2</span>
          <span class="ysl-title-bar__text">나를 도와주는 중심 기운과 보조 기운</span>
        </div>

        <!-- 본문 영역 -->
        <div class="ysl-content">
          <div class="ysl03-body">
            <!-- ── 1) 용신 일러스트 카드 ── -->
            <div class="ysl03-illust">
              <!-- 센터 데코 (말풍선 모양) -->
              <img
                :src="ImgCenterDeco"
                alt=""
                class="ysl03-illust__center-deco"
                :style="{ '--fill-0': decoColor }"
              />

              <!-- 이름 하이라이트 배경 -->
              <div class="ysl03-illust__highlight" :style="{ background: highlightColor }" />

              <!-- 4 코너 카드 (장식용 둥근 사각형) -->
              <div class="ysl03-corner ysl03-corner--tl" />
              <div class="ysl03-corner ysl03-corner--bl" />
              <div class="ysl03-corner ysl03-corner--tr" />
              <div class="ysl03-corner ysl03-corner--br" />

              <!-- 센터: 용신 이름 + 설명 -->
              <p class="ysl03-illust__name">{{ yongsinInfo.name }}</p>
              <p class="ysl03-illust__desc">{{ yongsinDescription }}</p>

              <!-- 왼쪽 상단: "가장 중요한 기운 = 용신" -->
              <div class="ysl03-illust__label ysl03-illust__label--lt">
                <p>가장 중요한 기운</p>
                <p>=</p>
                <p>용신</p>
              </div>

              <!-- 왼쪽 하단: 한자 -->
              <div class="ysl03-illust__label ysl03-illust__label--lb">
                <p class="ysl03-illust__hanja">{{ yongsinHanja.char }}</p>
                <p class="ysl03-illust__hanja-reading">({{ yongsinHanja.reading }})</p>
              </div>

              <!-- 오른쪽 상단: 오행 기운 + 자연물 -->
              <div class="ysl03-illust__label ysl03-illust__label--rt">
                <p class="ysl03-illust__el-label">{{ yongsinInfo.element }}의 기운</p>
                <p class="ysl03-illust__el-nature">{{ yongsinNature }}</p>
              </div>

              <!-- 오른쪽 하단: 태그 -->
              <p class="ysl03-illust__label ysl03-illust__label--rb">#{{ yongsinInfo.keyword }}</p>
            </div>

            <!-- ── 2) 용신 섹션 (뱃지 + 설명 카드) ── -->
            <div class="ysl03-section">
              <div class="ysl03-badge" :style="{ background: yongsinColor }">
                <span>중심 기운 = 용신</span>
              </div>
              <div class="ysl03-card">
                <div
                  class="ysl03-card__inner"
                  :style="{ '--yongsin-color': yongsinColor }"
                >
                  <template v-if="yongsinDescParagraphs">
                    <div
                      v-for="(para, pi) in yongsinDescParagraphs"
                      :key="pi"
                      class="ysl03-desc-group"
                    >
                      <p v-for="(line, li) in para" :key="li" v-html="line" />
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- ── 3) 희신 섹션 (뱃지 + 정보 카드) ── -->
            <div class="ysl03-section">
              <div class="ysl03-badge ysl03-badge--heesin" :style="{ background: heesinColor }">
                <span>보조 기운 = 희신</span>
              </div>
              <div class="ysl03-card">
                <!-- 정보 행 -->
                <div class="ysl03-heesin-info-row">
                  <div class="ysl03-heesin-info">
                    <div class="ysl03-heesin-info__item">
                      <p class="ysl03-heesin-info__label">{{ heesinInfo.element }}의 기운</p>
                      <p class="ysl03-heesin-info__sub">{{ heesinNature }}</p>
                    </div>
                    <div class="ysl03-heesin-info__divider" />
                    <p class="ysl03-heesin-info__name">{{ heesinInfo.name }}</p>
                    <div class="ysl03-heesin-info__divider" />
                    <div class="ysl03-heesin-info__item">
                      <p class="ysl03-heesin-info__hanja">{{ heesinHanja.char }}</p>
                      <p class="ysl03-heesin-info__hanja-reading">{{ heesinHanja.reading }}</p>
                    </div>
                    <div class="ysl03-heesin-info__divider" />
                    <p class="ysl03-heesin-info__tag">#{{ heesinInfo.keyword }}</p>
                  </div>
                </div>

                <!-- 설명 텍스트 -->
                <div class="ysl03-heesin-desc">
                  <template v-if="heesinDescParagraphs">
                    <div
                      v-for="(para, pi) in heesinDescParagraphs"
                      :key="pi"
                      class="ysl03-desc-group"
                    >
                      <p v-for="(line, li) in para" :key="li" v-html="line" />
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- ── 4) 두 기운의 관계 ── -->
            <div
              v-if="isSangsaeng"
              class="ysl03-relation"
              :style="{
                borderColor: yongsinColor,
                background: relationBgColor,
                '--yongsin-color': yongsinColor,
                '--heesin-color': heesinColor,
              }"
            >
              <p class="ysl03-relation__title">두 기운의 관계</p>

              <div class="ysl03-relation__circles">
                <span
                  class="ysl03-relation__circle"
                  :style="{ background: yongsinColor }"
                >
                  {{ ELEMENT_HANJA_TITLE_MAP[yongsinInfo.element] }}
                </span>
                <svg
                  class="ysl03-relation__arrow"
                  viewBox="0 0 30 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="0"
                    y1="6"
                    x2="24"
                    y2="6"
                    stroke="#333"
                    stroke-width="1.5"
                  />
                  <path d="M24 1L29 6L24 11" stroke="#333" stroke-width="1.5" fill="none" />
                </svg>
                <span
                  class="ysl03-relation__circle"
                  :style="{ background: heesinColor }"
                >
                  {{ ELEMENT_HANJA_TITLE_MAP[heesinInfo.element] }}
                </span>
              </div>

              <div class="ysl03-relation__desc">
                <p v-html="relationDescHtml" />
                <p v-for="(line, i) in relationTipLines" :key="i">{{ line }}</p>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <!-- 타이틀 바 -->
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">3</span>
          <span class="ysl-title-bar__text">오행의 성향과 나에게 힘을 주는 오행</span>
        </div>

        <!-- 본문 영역 -->
        <div class="ysl-content">
          <div class="ysl03-right-body">
            <!-- ── 가이드 말풍선 ── -->
            <div class="ysl03-guide">
              <div class="ysl03-guide__cat-wrap">
                <img :src="ImgCatGuide" alt="" class="ysl03-guide__cat" />
              </div>
              <div class="ysl03-guide__bubble-wrap">
                <!-- 꼬리 삼각형 (왼쪽 방향) -->
                <svg
                  class="ysl03-guide__tail"
                  viewBox="0 0 21 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.77 1.78C8.82-0.59 12.19-0.59 13.25 1.78L20.76 18.65C21.64 20.63 20.19 22.87 18.02 22.87H3C0.83 22.87-0.62 20.63 0.26 18.65L7.77 1.78Z"
                    fill="#FFF6ED"
                  />
                </svg>
                <!-- 말풍선 본체 -->
                <div class="ysl03-guide__bubble">
                  <p class="ysl03-guide__title">{{ guideBanner?.title || '사계절이 각자의 역할이 있듯이, 오행도 마찬가지예요' }}</p>
                  <p class="ysl03-guide__desc">
                    <template v-if="guideBanner?.lines?.length">
                      <template v-for="(line, i) in guideBanner.lines" :key="i">
                        {{ line }}<br v-if="i < guideBanner.lines.length - 1" />
                      </template>
                    </template>
                    <template v-else>
                      어떤 오행이 좋거나 나쁜 것이 아니라,<br />
                      내 사주에서 어떤 기운이 많고 적은지를 알면<br />
                      부족한 부분을 자연스럽게 채워갈 수 있습니다.
                    </template>
                  </p>
                </div>
              </div>
            </div>

            <!-- ── 오행 카드 리스트 ── -->
            <div class="ysl03-cards-list">
              <div
                v-for="card in ELEMENT_CARDS"
                :key="card.element"
                class="ysl03-element-card"
                :class="{
                  'ysl03-element-card--yongsin': card.isYongsin ?? (card.element === yongsinInfo.element),
                  'ysl03-element-card--heesin': card.isHeesin ?? (card.element === heesinInfo.element),
                }"
                :style="{
                  '--el-color': ELEMENT_COLOR_MAP[card.element],
                  '--el-border':
                    (card.isYongsin ?? (card.element === yongsinInfo.element))
                      ? ELEMENT_COLOR_MAP[card.element]
                      : (card.isHeesin ?? (card.element === heesinInfo.element))
                        ? ELEMENT_COLOR_MAP[card.element]
                        : 'transparent',
                }"
              >
                <!-- 헤더: 아이콘 + 오행 이름 + 용신/희신 라벨 + 오상 뱃지 -->
                <div class="ysl03-element-card__header">
                  <div class="ysl03-element-card__title-area">
                    <img
                      :src="ELEMENT_ICON_MAP[card.element].src"
                      alt=""
                      class="ysl03-element-card__header-icon"
                    />
                    <p class="ysl03-element-card__name">
                      {{ card.element }}(<span class="medium">{{
                        ELEMENT_HANJA_TITLE_MAP[card.element]
                      }}</span
                      >)
                    </p>
                    <!-- 내 용신/희신 라벨 -->
                    <div
                      v-if="card.isYongsin ?? (card.element === yongsinInfo.element)"
                      class="ysl03-element-card__label"
                    >
                      <img :src="ArrowLabel" alt="" class="ysl03-element-card__label-arrow" />
                      <span class="ysl03-element-card__label-text">내 용신</span>
                    </div>
                    <div
                      v-else-if="card.isHeesin ?? (card.element === heesinInfo.element)"
                      class="ysl03-element-card__label"
                    >
                      <img :src="ArrowLabel" alt="" class="ysl03-element-card__label-arrow" />
                      <span class="ysl03-element-card__label-text">내 희신</span>
                    </div>
                  </div>
                  <div class="ysl03-element-card__virtue">
                    <span
                      class="ysl03-element-card__virtue-badge"
                      :style="{ background: ELEMENT_COLOR_MAP[card.element] }"
                    >
                      {{ ELEMENT_VIRTUE_MAP[card.element] }}
                    </span>
                    <span
                      class="ysl03-element-card__virtue-text"
                      :style="{ color: ELEMENT_COLOR_MAP[card.element] }"
                    >
                      {{ ELEMENT_HANJA_MAP[card.element].char }} ({{
                        ELEMENT_HANJA_MAP[card.element].reading
                      }})
                    </span>
                  </div>
                </div>

                <!-- 설명 -->
                <div class="ysl03-element-card__desc">
                  <p>
                    {{ card.element }}({{ ELEMENT_HANJA_TITLE_MAP[card.element] }}){{
                      card.element === '토'
                        ? '는 '
                        : card.element === '금'
                          ? '은 '
                          : card.element === '수'
                            ? '는 '
                            : card.element === '화'
                              ? '는 '
                              : '은 '
                    }}<span class="medium">{{ card.descBold }}</span
                    >{{ card.descSuffix }}
                  </p>
                  <p>{{ card.descSub }}</p>
                </div>

                <!-- 해시태그 -->
                <p
                  class="ysl03-element-card__tag"
                  :style="{ color: ELEMENT_COLOR_MAP[card.element] }"
                >
                  {{ card.hashtag }}
                </p>

                <!-- 비율 바 -->
                <div class="ysl03-element-card__ratio-row">
                  <span class="ysl03-element-card__ratio-value">
                    {{ ratioMap[card.element] ?? 0 }}%
                  </span>
                  <div class="ysl03-element-card__ratio-track">
                    <div class="ysl03-element-card__ratio-bg" />
                    <div
                      class="ysl03-element-card__ratio-fill"
                      :style="{
                        width: animated ? `${ratioMap[card.element] ?? 0}%` : '0%',
                        background: ELEMENT_COLOR_MAP[card.element],
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- ── 다크 요약 섹션 (칠판) ── -->
            <div
              v-if="ohengSummaryLines.length > 0 || lowestElement"
              class="ysl03-dark-summary"
            >
              <!-- 칠판 배경 이미지 -->
              <img :src="ImgChalkboard" alt="" class="ysl03-dark-summary__bg" />

              <!-- 열림 따옴표 -->
              <svg class="ysl03-dark-summary__quote ysl03-dark-summary__quote--open" width="18" height="13.5" viewBox="0 0 18 13.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.625 2.25L6.75 0H4.5C2.014 0 0 3.139 0 5.625V13.5H7.875V5.625H3.375C3.375 2.25 5.625 2.25 5.625 2.25ZM13.5 5.625C13.5 2.25 15.75 2.25 15.75 2.25L16.875 0H14.625C12.139 0 10.125 3.139 10.125 5.625V13.5H18V5.625H13.5Z" fill="white" />
              </svg>

              <!-- 요약 텍스트 -->
              <div class="ysl03-dark-summary__text">
                <p v-for="(line, idx) in ohengSummaryLines" :key="idx" v-html="line" />
              </div>

              <!-- 닫힘 따옴표 -->
              <svg class="ysl03-dark-summary__quote ysl03-dark-summary__quote--close" width="18" height="13.5" viewBox="0 0 18 13.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.375 11.25L11.25 13.5H13.5C15.986 13.5 18 10.361 18 7.875V0H10.125V7.875H14.625C14.625 11.25 12.375 11.25 12.375 11.25ZM4.5 7.875C4.5 11.25 2.25 11.25 2.25 11.25L1.125 13.5H3.375C5.861 13.5 7.875 10.361 7.875 7.875V0H0V7.875H4.5Z" fill="white" />
              </svg>

              <!-- 하단 안심멘트 (API) -->
              <div v-if="ansimMessage" class="ysl03-dark-summary__advice">
                <p v-for="(line, i) in ansimMessage.split('\n')" :key="i">{{ line }}</p>
              </div>
              <div v-else-if="lowestElement" class="ysl03-dark-summary__advice">
                <p>{{ lowestElement.element }}({{ lowestElementHanja }})의 비율이 적더라도 걱정하지 마세요.</p>
                <p>대운에서 채워지거나, 용신의 균형으로 자연스럽게 보완됩니다.</p>
              </div>

              <!-- 고양이 선생님 일러스트 -->
              <img :src="ImgCatTeacher" alt="" class="ysl03-dark-summary__cat" />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
$ysl-primary: #4e80d6;
$ysl-primary-light: rgba(78, 128, 214, 0.05);
$ysl-text: #333;

// ── 공통 (content1/2와 동일 구조) ──
.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

// ── 타이틀 바 (번호 뱃지 스타일) ──
.ysl-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: #fff;
  flex-shrink: 0;

  &__num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 11px;
    background: #4b73b2;
    color: #fff;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    flex-shrink: 0;
  }

  &__text {
    color: #4b73b2;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

// ── 본문 영역 ──
.ysl-content {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
  padding-bottom: 100px;
  background: #f8f8f8;
}

// ── content3 바디 ──
.ysl03-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

// ═══════════════════════════════
// 1) 용신 일러스트 카드
// ═══════════════════════════════
// 일러스트 기준: 513 x 232
.ysl03-illust {
  position: relative;
  width: 100%;
  max-width: 513px;
  aspect-ratio: 513 / 232;
  flex-shrink: 0;
  background: #f8f8f8;
  container-type: inline-size;

  // 센터 데코 (말풍선 모양 SVG)
  &__center-deco {
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15));
    position: absolute;
    left: 33.14%;
    top: 0;
    width: 33.92%;
    height: 100%;
    pointer-events: none;
  }

  // 이름 하이라이트 배경
  &__highlight {
    position: absolute;
    left: 39.57%;
    top: 14.2%;
    width: 21.05%;
    height: 15.5%;
    border-radius: 5px;
  }

  // 센터 이름
  &__name {
    position: absolute;
    left: 50%;
    top: 16%;
    transform: translateX(-50%);
    margin: 0;
    font-family: Pretendard;
    font-size: clamp(14px, 3.9cqw, 20px);
    font-weight: 700;
    line-height: 1.4;
    color: $ysl-text;
    text-align: center;
    white-space: nowrap;
    z-index: 1;
  }

  // 센터 설명 (말풍선 SVG 내부: left 33.14% ~ width 33.92%)
  &__desc {
    position: absolute;
    left: 36%;
    right: 36%;
    top: 38.4%;
    text-align: center;
    font-family: Pretendard;
    font-size: clamp(12px, 3.5cqw, 18px);
    font-weight: 600;
    line-height: 1.55;
    color: $ysl-text;
    word-break: keep-all;
    z-index: 1;
    margin: 0;
  }

  // 텍스트 라벨 공통
  &__label {
    position: absolute;
    text-align: center;
    font-family: Pretendard;
    font-size: clamp(10px, 2.9cqw, 15px);
    font-weight: 600;
    line-height: 1.6;
    color: $ysl-text;
    white-space: nowrap;
    z-index: 1;

    p {
      margin: 0;
    }

    // 왼쪽 상단: "가장 중요한 기운 = 용신"
    &--lt {
      left: 15.4%;
      top: 10.3%;
      transform: translateX(-50%);
    }

    // 왼쪽 하단: 한자
    &--lb {
      left: 15.4%;
      top: 62.1%;
      transform: translateX(-50%);
    }

    // 오른쪽 상단: 오행 기운
    &--rt {
      left: 84.8%;
      top: 14.7%;
      transform: translateX(-50%);
    }

    // 오른쪽 하단: 태그
    &--rb {
      left: 84.8%;
      top: 65.5%;
      transform: translateX(-50%);
      font-weight: 600;
    }
  }

  // 한자 (큰 글씨)
  &__hanja {
    font-family: Pretendard;
    font-size: clamp(16px, 4.9cqw, 25px);
    font-weight: 500;
    line-height: 1.4;
    color: $ysl-text;
    margin: 0;
  }

  &__hanja-reading {
    font-family: Pretendard;
    font-size: clamp(10px, 2.9cqw, 15px);
    font-weight: 600;
    line-height: 1.4;
    color: $ysl-text;
    margin: 0;
  }

  // 오행 기운 라벨
  &__el-label {
    font-weight: 600;
    line-height: 1.3;
  }

  &__el-nature {
    font-family: Pretendard;
    font-size: clamp(10px, 2.9cqw, 15px);
    font-weight: 500;
    line-height: 1.6;
    color: #575757;
  }
}

// 4 코너 장식 카드 (CSS 둥근 사각형) — % 기반
.ysl03-corner {
  position: absolute;
  border-radius: 20px;
  z-index: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);

  &--tl {
    left: 0;
    top: 0;
    width: 30.6%;
    height: 51.3%;
    background: #e5eaf6;
  }

  &--bl {
    left: 0;
    top: 55.6%;
    width: 30.6%;
    height: 35.8%;
    background: #f8e3ea;
  }

  &--tr {
    left: 69.4%;
    top: 0;
    width: 30.6%;
    height: 45.7%;
    background: #e9e6f7;
  }

  &--br {
    left: 69.4%;
    top: 50%;
    width: 30.6%;
    height: 41.4%;
    background: #e6f4f6;
  }
}

// ═══════════════════════════════
// 2) 뱃지 + 카드 통합 섹션
// ═══════════════════════════════
.ysl03-section {
  position: relative;
  width: 552px;
  max-width: 100%;
  padding-top: 13px; // 뱃지가 카드 위에 겹치는 공간
}

.ysl03-badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 148px;
  height: 26px;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;

  span {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #fff;
  }

  // 왼쪽 삼각형 (접힌 효과)
  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 0;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-right: 10px solid;
    border-right-color: inherit;
  }
}

.ysl03-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  padding: 27px 26px;

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    background: #f8f8f8;
    border-radius: 10px;
    padding: 16px;
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    word-break: keep-all;

    p {
      margin: 0;
    }

    :deep(.bold) {
      font-weight: 500;
      color: $ysl-text;
    }

    :deep(.ysl-keyword) {
      font-weight: 600;
      color: var(--yongsin-color, $ysl-text);
    }
  }
}

.ysl03-desc-group {
  p {
    margin: 0;
  }
}

// ═══════════════════════════════
// 3) 희신 정보 행 + 설명
// ═══════════════════════════════
.ysl03-heesin-info-row {
  background: #f8f8f8;
  border-radius: 10px;
  padding: 32px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.ysl03-heesin-desc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  color: #575757;
  word-break: keep-all;

  p {
    margin: 0;
  }

  :deep(.bold) {
    font-weight: 500;
    color: $ysl-text;
  }
}

// 희신 정보 행 (가로 배치)
.ysl03-heesin-info {
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
  justify-content: center;

  &__item {
    text-align: center;
    white-space: nowrap;
  }

  &__label {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: #575757;
    margin: 0;
  }

  &__sub {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: #757575;
    margin: 0;
  }

  &__divider {
    width: 1px;
    height: 31px;
    background: #ebebeb;
    flex-shrink: 0;
  }

  &__name {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #575757;
    margin: 0;
    white-space: nowrap;
  }

  &__hanja {
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    color: #575757;
    margin: 0;
  }

  &__hanja-reading {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #575757;
    margin: 0;
  }

  &__tag {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #575757;
    margin: 0;
    white-space: nowrap;
  }
}

// ═══════════════════════════════
// 4) 두 기운의 관계 카드
// ═══════════════════════════════
.ysl03-relation {
  width: 552px;
  max-width: 100%;
  border: 1px solid;
  border-radius: 20px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 27px 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: $ysl-text;
    text-align: center;
    margin: 0;
  }

  &__circles {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    border-radius: 40px;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #fff;
  }

  &__arrow {
    width: 25px;
    height: 12px;
    flex-shrink: 0;
  }

  &__desc {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;
    word-break: keep-all;

    p {
      margin: 0;
    }
  }

  :deep(.ysl-relation-yongsin) {
    font-weight: 700;
    color: var(--yongsin-color);
  }

  :deep(.ysl-relation-heesin) {
    font-weight: 700;
    color: var(--heesin-color);
  }

  :deep(.ysl-relation-bold) {
    font-weight: 700;
  }
}

// ═══════════════════════════════
// 오른쪽 페이지
// ═══════════════════════════════
.ysl03-right-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

// ── 가이드 말풍선 ──
.ysl03-guide {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;

  &__cat-wrap {
    width: 117px;
    height: 96px;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__cat {
    width: 100%;
    height: auto;
    object-fit: cover;
    margin-top: -46%;
  }

  &__bubble-wrap {
    display: flex;
    align-items: center;
    flex: 0 1 auto;
    min-width: 0;
    filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.15));
  }

  &__tail {
    width: 28px;
    height: 36px;
    flex-shrink: 0;
    transform: rotate(-90deg);
    margin-right: -8px;
    margin-top: 24px;
  }

  &__bubble {
    background: #fff6ed;
    border-radius: 12px;
    padding: 12px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    flex: 1 1 0;
    max-width: 376px;
  }

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #db9823;
    margin: 0;
    text-align: center;
    word-break: keep-all;
  }

  &__desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;
    text-align: center;
    margin: 0;
  }
}

// ── 오행 카드 리스트 ──
.ysl03-cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ysl03-element-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 25px 30px;
  border-radius: 12px;
  overflow: hidden;

  // 오행 색상별 배경
  &[style*='--el-color: #5e9f1c'] {
    background: linear-gradient(90deg, rgba(94, 159, 28, 0.08), rgba(94, 159, 28, 0.08)), #fff;
  }

  &[style*='--el-color: #c23535'] {
    background: linear-gradient(90deg, rgba(194, 53, 53, 0.08), rgba(194, 53, 53, 0.08)), #fff;
  }

  &[style*='--el-color: #db9823'] {
    background: linear-gradient(90deg, rgba(219, 152, 35, 0.08), rgba(219, 152, 35, 0.08)), #fff;
  }

  &[style*='--el-color: #949494'] {
    background: linear-gradient(90deg, rgba(148, 148, 148, 0.05), rgba(148, 148, 148, 0.05)), #fff;
  }

  &[style*='--el-color: #404040'] {
    background: linear-gradient(90deg, rgba(64, 64, 64, 0.08), rgba(64, 64, 64, 0.08)), #fff;
  }

  // 용신/희신 보더
  &--yongsin,
  &--heesin {
    border: 1.5px solid var(--el-border);
  }

  // 헤더
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__title-area {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
  }

  &__header-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__name {
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    color: $ysl-text;
    margin: 0;
    white-space: nowrap;

    .medium {
      font-weight: 500;
    }
  }

  // 내 용신/희신 라벨
  &__label {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-left: 3px;
  }

  &__label-arrow {
    width: 26px;
    height: 15px;
    object-fit: contain;
    transform: rotate(170.2deg) scaleY(-1);
    margin-top: -16px;
    margin-right: 3px;
  }

  &__label-text {
    font-family: 'GyeonggiTitle', Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    color: $ysl-text;
    white-space: nowrap;
  }

  // 오상 뱃지
  &__virtue {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
  }

  &__virtue-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 40px;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #fff;
  }

  &__virtue-text {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
  }

  // 설명
  &__desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;
    word-break: keep-all;

    p {
      margin: 0;
    }

    .medium {
      font-weight: 500;
      color: $ysl-text;
    }
  }

  // 해시태그
  &__tag {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
  }

  // 비율 바
  &__ratio-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__ratio-value {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: #757575;
    white-space: nowrap;
    min-width: 32px;
    text-align: right;
  }

  &__ratio-track {
    position: relative;
    width: 242px;
    height: 10px;
  }

  &__ratio-bg {
    position: absolute;
    inset: 0;
    background: #ebebeb;
    border-radius: 10px;
  }

  &__ratio-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: 10px;
    transition: width 0.8s ease-out;
  }
}

// ── 다크 요약 섹션 (칠판) ──
.ysl03-dark-summary {
  position: relative;
  width: 400px;
  max-width: 100%;
  margin: 0 auto;
  aspect-ratio: 343 / 475;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8% 8% 16%;
  gap: 4px;
  box-sizing: border-box;

  &__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    z-index: 0;
  }

  &__quote {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    opacity: 0.8;
  }

  &__text {
    position: relative;
    z-index: 1;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #fff;
    text-align: center;
    word-break: keep-all;
    overflow-wrap: break-word;
    width: 100%;
    min-width: 0;

    p {
      margin: 0;
    }

    :deep(.highlight) {
      font-weight: 700;
      color: #ffde25;
    }
  }

  &__advice {
    position: relative;
    z-index: 1;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: #f8f8f8;
    text-align: center;
    margin: 8px 0 0;
    word-break: keep-all;
    overflow-wrap: break-word;
    width: 100%;
    min-width: 0;

    p {
      margin: 0;
    }
  }

  &__cat {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 114px;
    height: 114px;
    object-fit: cover;
    pointer-events: none;
    z-index: 1;
  }
}

// ═══════════════════════════════
// 모바일 반응형
// ═══════════════════════════════
@media (max-width: 600px) {
  .ysl03-guide {
    flex-direction: column;
    align-items: center;
    gap: 0;

    &__cat-wrap {
      width: 117px;
      height: 96px;
    }

    &__bubble-wrap {
      flex-direction: column;
      align-items: center;
      width: 100%;
    }

    &__tail {
      width: 21px;
      height: 23px;
      transform: none;
      margin: 0;
    }

    &__bubble {
      width: 100%;
      max-width: none;
      padding: 15px 24px;
      align-items: center;
      margin-top: -6px;
    }

    &__title {
      font-size: 13px;
      white-space: normal;
      text-align: center;
    }

    &__desc {
      font-size: 12px;
      line-height: 20px;
    }
  }

  .ysl03-element-card {
    padding: 20px 16px;

    &__header {
      gap: 6px;
    }

    &__header-icon {
      width: 26px;
      height: 26px;
    }

    &__name {
      font-size: 17px;
    }

    &__label-text {
      font-size: 13px;
    }

    &__virtue-text {
      font-size: 13px;
    }

    &__desc {
      font-size: 14px;
    }

    &__tag {
      font-size: 14px;
    }

    &__ratio-track {
      width: 180px;
    }
  }

  .ysl03-dark-summary {
    padding: 12% 10% 14%;

    &__text {
      font-size: 14px;
      line-height: 20px;
    }

    &__advice {
      font-size: 13px;
    }

    &__cat {
      width: 22%;
      height: auto;
      aspect-ratio: 1;
    }
  }
}
</style>
